import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Bullet implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  
  // Bullet properties
  private readonly BULLET_SIZE = 4;
  private readonly BULLET_SPEED = 400; // pixels per second
  private animationTimer: number = 0;

  constructor(
    id: string,
    x: number,
    y: number,
    directionX: number,
    directionY: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.id = id;
    this.position = { x, y };
    this.size = { x: this.BULLET_SIZE, y: this.BULLET_SIZE };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    
    // Set velocity based on direction
    this.velocity = {
      x: directionX * this.BULLET_SPEED,
      y: directionY * this.BULLET_SPEED
    };
  }

  public update(deltaTime: number): void {
    // Update animation timer
    this.animationTimer += deltaTime;
    
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // Mark as inactive if off screen (with some margin)
    if (this.position.x < -50 || this.position.x > 850 || 
        this.position.y < -50 || this.position.y > 650) {
      this.active = false;
    }
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x - this.BULLET_SIZE / 2,
      y: this.position.y - this.BULLET_SIZE / 2,
      width: this.BULLET_SIZE,
      height: this.BULLET_SIZE
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;
    
    const centerX = this.position.x;
    const centerY = this.position.y;
    const halfSize = this.BULLET_SIZE / 2;
    
    // Main bullet body (glowing core)
    this.renderManager.drawEllipse(
      centerX - halfSize,
      centerY - halfSize,
      halfSize,
      halfSize,
      '#FFFFFF',
      true
    );
    
    // Energy trail effect
    const trailLength = 8;
    const trailAlpha = 0.6;
    
    // Calculate trail direction (opposite to velocity)
    const trailDirX = this.velocity.x !== 0 ? -this.velocity.x / Math.abs(this.velocity.x) : 0;
    const trailDirY = this.velocity.y !== 0 ? -this.velocity.y / Math.abs(this.velocity.y) : 0;
    
    // Draw trail segments with decreasing opacity
    for (let i = 1; i <= 3; i++) {
      const trailX = centerX + trailDirX * i * 3;
      const trailY = centerY + trailDirY * i * 3;
      const trailSize = halfSize * (1 - i * 0.2);
      const alpha = trailAlpha * (1 - i * 0.3);
      
      this.renderManager.drawEllipse(
        trailX - trailSize,
        trailY - trailSize,
        trailSize,
        trailSize,
        `rgba(0, 255, 255, ${alpha})`,
        true
      );
    }
    
    // Pulsing outer glow
    const glowIntensity = 0.3 + Math.sin(this.animationTimer * 8) * 0.2;
    this.renderManager.drawEllipse(
      centerX - halfSize * 1.5,
      centerY - halfSize * 1.5,
      halfSize * 1.5,
      halfSize * 1.5,
      `rgba(0, 255, 255, ${glowIntensity})`,
      false,
      2
    );
  }
}